import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { styled } from '@mui/material/styles'
import { useTheme, Box, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import { endOfWeek, isSameWeek, startOfWeek, startOfMonth, format } from 'date-fns'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { FRONTEND_DATE_FORMAT_DATE_FNS } from 'settings/constants/date'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { Calendar } from '@styled-icons/heroicons-outline'
import { Z_INDEX } from 'theme/zIndex'
import PickersDayWithCount from 'components/configured-service/tabs/MapRoutingTab/components/PickersDayWithCount'
import { useServiceWorkOrderCountsByMonth } from 'components/configured-service/tabs/MapRoutingTab/hooks/useServiceWorkOrderCountsByMonth'
import { useCSDetailsURLData } from 'api/configured-service/useCSDetailsURLData'

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'isHovered',
})(({ theme, isSelected, isHovered, day }) => ({
  borderRadius: 0,
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
    },
  }),
  ...(isHovered && {
    backgroundColor: theme.palette.primary.light,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.light,
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.primary.dark,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
  }),
  ...(day.getDay() === 0 && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.getDay() === 6 && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
}))

const isInSameWeek = (dayA, dayB) => {
  if (dayB == null) return false
  return isSameWeek(dayB, dayA)
}

function Day(props) {
  const { day, selectedDay, hoveredDay, ...other } = props

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay)}
      isHovered={isInSameWeek(day, hoveredDay)}
    />
  )
}

const WeekPickerDisplay = React.forwardRef((props, ref) => {
  const { displayValue, onClick, inputRef, ...other } = props
  const theme = useTheme()

  return (
    <Box
      ref={ref}
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="center"
      height={40}
      px={1.5}
      bgcolor="#ffffff"
      gap={1}
      width="auto"
      borderRadius="50px"
      sx={{
        cursor: 'pointer',
        boxShadow: theme.shadows[2],
        '&:hover': {
          bgcolor: theme.palette.grey[50],
        },
      }}
      {...other}
    >
      <input ref={inputRef} style={{ display: 'none' }} />
      <Typography variant="body1" sx={{ cursor: 'pointer', fontWeight: 500 }}>
        {displayValue}
      </Typography>
      <Calendar width={20} height={20} color={theme.palette.action.active} />
    </Box>
  )
})

const WeekPicker = ({ showServiceWorkOrderCounts = false }) => {
  const theme = useTheme()
  const { id: configuredServiceId } = useCSDetailsURLData()
  const [hoveredDay, setHoveredDay] = useState(null)
  const [visibleMonth, setVisibleMonth] = useState(null)
  const { setValue, watch } = useFormContext()
  // We use startDate as the primary value for the picker
  const selectedDate = watch('startDate')
  const endDate = watch('endDate')

  // Safely parse dates and memoize to ensure stable references
  const formattedSelectedDate = useMemo(() => formatDateToBEFormatDateFns(selectedDate), [selectedDate])
  const formattedEndDate = useMemo(() => formatDateToBEFormatDateFns(endDate), [endDate])

  const parsedDate = useMemo(() => (selectedDate ? new Date(selectedDate) : null), [formattedSelectedDate])
  const parsedEndDate = useMemo(() => (endDate ? new Date(endDate) : null), [formattedEndDate])

  // Calculate effective end date if missing (fix for pre-filled data)
  const effectiveEndDate = useMemo(() => parsedEndDate || (parsedDate ? endOfWeek(parsedDate) : null), [parsedEndDate, parsedDate])

  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = React.useRef(null)

  // Seed from selected date on first open — MUI does not fire onMonthChange for the initial month.
  const effectiveVisibleMonth = useMemo(() => {
    if (visibleMonth) return visibleMonth
    if (parsedDate) return startOfMonth(parsedDate)
    return null
  }, [visibleMonth, parsedDate])

  const { countsByDate } = useServiceWorkOrderCountsByMonth({
    configuredServiceId: showServiceWorkOrderCounts ? configuredServiceId : null,
    visibleMonth: showServiceWorkOrderCounts ? effectiveVisibleMonth : null,
  })

  const handleDateChange = date => {
    if (date) {
      setValue('startDate', startOfWeek(date))
      setValue('endDate', endOfWeek(date))
    } else {
      setValue('startDate', null)
      setValue('endDate', null)
    }
  }

  const handleMonthChange = newMonth => {
    setVisibleMonth(startOfMonth(newMonth))
  }

  const displayValue =
    parsedDate && effectiveEndDate
      ? `${format(parsedDate, FRONTEND_DATE_FORMAT_DATE_FNS)} - ${format(effectiveEndDate, FRONTEND_DATE_FORMAT_DATE_FNS)}`
      : ''

  const DaySlot = showServiceWorkOrderCounts ? PickersDayWithCount : Day

  const daySlotProps = ownerState => ({
    selectedDay: parsedDate,
    hoveredDay,
    ...(showServiceWorkOrderCounts && { countsByDate }),
    onPointerEnter: () => setHoveredDay(ownerState.day),
    onPointerLeave: () => setHoveredDay(null),
  })

  return (
    <Box ref={anchorRef} display="inline-block">
      <DesktopDatePicker
        value={parsedDate || null}
        onChange={handleDateChange}
        {...(showServiceWorkOrderCounts && { onMonthChange: handleMonthChange })}
        format={FRONTEND_DATE_FORMAT_DATE_FNS}
        showDaysOutsideCurrentMonth
        enableAccessibleFieldDOMStructure={false}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        referenceDate={parsedDate || undefined}
        slots={{
          day: DaySlot,
          textField: WeekPickerDisplay,
          openPickerButton: () => null,
        }}
        slotProps={{
          day: daySlotProps,
          textField: {
            displayValue,
            onClick: () => setIsOpen(true),
          },
          popper: {
            anchorEl: anchorRef.current,
            sx: {
              zIndex: Z_INDEX.MENU(theme),
            },
          },
        }}
      />
    </Box>
  )
}

WeekPicker.propTypes = {
  showServiceWorkOrderCounts: PropTypes.bool,
}

export default WeekPicker
